import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Grid,
    Select,
    useTheme,
    Flex,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import dayjs from 'dayjs';
import api from '../../../auth/apiClient';
import type { OdooVsSaktiFilters } from '../types';

interface Company {
    id: number;
    name: string;
}

interface Estate {
    id: number;
    name: string;
    odoo_id: number;
    company_id?: number;
}

interface Division {
    id: number;
    name: string;
    estate_id: number;
    odoo_id: number;
}

interface FilterOdooVsSaktiProps {
    onFiltersChange: (filters: OdooVsSaktiFilters) => void;
    isLoading?: boolean;
}

export const FilterOdooVsSakti: React.FC<FilterOdooVsSaktiProps> = ({
    onFiltersChange,
    isLoading = false,
}) => {
    const theme = useTheme();
    const today = dayjs().format('YYYY-MM-DD');

    const [startDate, setStartDate] = useState<string>(today);
    const [endDate, setEndDate] = useState<string>(today);

    const [companies, setCompanies] = useState<Company[]>([]);
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [allDivisions, setAllDivisions] = useState<Division[]>([]);

    const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
    const [selectedEstateId, setSelectedEstateId] = useState<number | undefined>(14);
    const [selectedDivisionId, setSelectedDivisionId] = useState<number | undefined>(undefined);

    const filteredEstates = useMemo(() => {
        if (!selectedCompanyId) return estates;
        return estates.filter((e) => e.company_id === selectedCompanyId);
    }, [estates, selectedCompanyId]);

    const inputStyles = {
        size: 'sm' as const,
        borderRadius: 'lg',
        borderColor: 'gray.200',
        _hover: { borderColor: 'gray.300' },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [optionsRes, estatesDbRes] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);

                const data = optionsRes.data;
                const estatesDbData = estatesDbRes.data;

                const fetchedCompanies: Company[] = data.companies || [];
                setCompanies(fetchedCompanies);
                setAllDivisions(data.divisis || []);

                const mergedEstates: Estate[] = (estatesDbData || []).map((estateDb: any) => {
                    const optionsEstate = (data.estates || []).find((e: any) => e.name === estateDb.name);
                    return {
                        id: estateDb.id,
                        name: estateDb.name,
                        company_id: estateDb.company_id,
                        odoo_id: optionsEstate?.odoo_id || estateDb.id,
                    };
                });
                setEstates(mergedEstates);

                if (fetchedCompanies.length > 0) {
                    const defaultCompany = fetchedCompanies[0];
                    setSelectedCompanyId(defaultCompany.id);

                    const estate14 = mergedEstates.find((e) => e.odoo_id === 14);
                    let defaultEstateId: number | undefined = 14;

                    if (!estate14 || estate14.company_id !== defaultCompany.id) {
                        defaultEstateId = undefined;
                    }

                    setSelectedEstateId(defaultEstateId);

                    const formattedDate = dayjs(today).format('YYYYMMDD');
                    onFiltersChange({
                        start_date: formattedDate,
                        end_date: formattedDate,
                        company_id: defaultCompany.id,
                        estate_id: defaultEstateId,
                    });
                }
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        if (!selectedEstateId) {
            setDivisions([]);
            return;
        }
        const selectedEstate = estates.find((e) => e.odoo_id === selectedEstateId);
        if (selectedEstate) {
            setDivisions(allDivisions.filter((d) => d.estate_id === selectedEstate.id));
        }
    }, [selectedEstateId, estates, allDivisions]);

    const handleApply = () => {
        onFiltersChange({
            start_date: dayjs(startDate).format('YYYYMMDD'),
            end_date: dayjs(endDate).format('YYYYMMDD'),
            company_id: selectedCompanyId,
            estate_id: selectedEstateId,
            division_id: selectedDivisionId,
        });
    };

    const handleReset = () => {
        setStartDate(today);
        setEndDate(today);

        let resetCompanyId: number | undefined = undefined;
        let defaultEstateId: number | undefined = 14;

        if (companies.length > 0) {
            resetCompanyId = companies[0].id;
            setSelectedCompanyId(resetCompanyId);

            const estate14 = estates.find((e) => e.odoo_id === 14);
            if (!estate14 || estate14.company_id !== resetCompanyId) {
                defaultEstateId = undefined;
            }
        } else {
            setSelectedCompanyId(undefined);
            defaultEstateId = undefined;
        }

        setSelectedEstateId(defaultEstateId);
        setSelectedDivisionId(undefined);

        onFiltersChange({
            start_date: dayjs(today).format('YYYYMMDD'),
            end_date: dayjs(today).format('YYYYMMDD'),
            company_id: resetCompanyId,
            estate_id: defaultEstateId,
            division_id: undefined,
        });
    };

    return (
        <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.100"
        >
            <VStack spacing={4} align="stretch">
                <Grid
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr) repeat(3, 1fr)',
                    }}
                    gap={4}
                >
                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Tanggal Mulai</FormLabel>
                        <Input
                            {...inputStyles}
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Tanggal Akhir</FormLabel>
                        <Input
                            {...inputStyles}
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Company</FormLabel>
                        <Select
                            {...inputStyles}
                            value={selectedCompanyId || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedCompanyId(val ? Number(val) : undefined);
                                setSelectedEstateId(undefined);
                                setSelectedDivisionId(undefined);
                            }}
                        >
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Estate</FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Semua Estate"
                            value={selectedEstateId || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedEstateId(val ? Number(val) : undefined);
                                setSelectedDivisionId(undefined);
                            }}
                        >
                            {filteredEstates.map((e) => (
                                <option key={e.id} value={e.odoo_id}>{e.name}</option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm" color="gray.600" mb={1}>Divisi</FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Semua Divisi"
                            value={selectedDivisionId || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedDivisionId(val ? Number(val) : undefined);
                            }}
                            isDisabled={!selectedEstateId || divisions.length === 0}
                        >
                            {divisions.map((d) => (
                                <option key={d.id} value={d.odoo_id}>{d.name}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Flex
                    gap={2}
                    justify={{ base: 'stretch', md: 'flex-end' }}
                    direction={{ base: 'column', sm: 'row' }}
                >
                    <Button
                        leftIcon={<MdClear />}
                        onClick={handleReset}
                        isDisabled={isLoading}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Reset
                    </Button>
                    <Button
                        leftIcon={<FiSearch />}
                        colorScheme="blue"
                        onClick={handleApply}
                        isLoading={isLoading}
                        size="sm"
                        borderRadius="lg"
                        flex={{ base: 1, sm: 'initial' }}
                    >
                        Terapkan Filter
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};
